import { useState } from "react";
import { History, RotateCcw, Ban, Printer, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ReceiptDialog, PaymentDialog } from "@/components/PaymentDialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtTime, canDo, PAY_LABELS, ORIGIN_LABELS } from "@/lib/api";
import { toast } from "sonner";

const STATUS = { completed: "", cancelled: "Cancelada", reopened: "Reaberta" };

export function RecentSales({ onReopen }) {
  const [open, setOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [changing, setChanging] = useState(null);
  const { session } = useAuth();
  const user = session.user;
  const load = () => api.get("/sales?limit=40").then((r) => setSales(r.data));
  const show = () => { setOpen(true); load(); };

  const act = async (s, kind) => {
    const reason = window.prompt(kind === "cancel" ? `Motivo do cancelamento da venda #${s.number}:` : `Motivo da reabertura da venda #${s.number}:`);
    if (reason === null) return;
    try {
      const { data } = await api.post(`/sales/${s.id}/${kind}`, { reason });
      toast.success(kind === "cancel" ? "Venda cancelada" : "Venda reaberta no carrinho");
      if (kind === "reopen") { onReopen(data); setOpen(false); } else load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const changePayments = async ({ payments }) => {
    await api.post(`/sales/${changing.id}/payments`, { payments });
    toast.success("Formas de pagamento alteradas");
    load();
  };

  return (
    <>
      <Button variant="outline" onClick={show} data-testid="recent-sales-btn"><History className="h-4 w-4 mr-1" />Vendas recentes</Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="recent-sales-sheet">
          <SheetHeader><SheetTitle>Vendas recentes</SheetTitle></SheetHeader>
          <ul className="mt-4 divide-y">
            {sales.map((s) => (
              <li key={s.id} className="py-3 space-y-2" data-testid={`recent-sale-${s.number}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-mono font-bold">#{s.number} <span className="font-sans font-normal text-muted-foreground">· {fmtTime(s.created_at)} · {ORIGIN_LABELS[s.origin]}</span></span>
                  <span className={`font-mono ${s.status !== "completed" ? "line-through text-muted-foreground" : "font-semibold"}`}>{brl(s.total)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{s.user_name} · {s.payments.map((p) => PAY_LABELS[p.method]).join(" + ")}{s.customer ? ` · ${s.customer.name}` : ""}</p>
                {s.status !== "completed" ? <p className="text-xs text-red-500">{STATUS[s.status]} por {s.cancelled_by}{s.cancel_reason ? ` — ${s.cancel_reason}` : ""}</p> : (
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setReceipt(s)} data-testid={`recent-sale-print-${s.number}`}><Printer className="h-4 w-4 mr-1" />Comprovante</Button>
                    {canDo(user, "change_payment") && <Button size="sm" variant="ghost" onClick={() => setChanging(s)} data-testid={`recent-sale-payment-${s.number}`}><CreditCard className="h-4 w-4 mr-1" />Pagamento</Button>}
                    {canDo(user, "cancel_sale") && <Button size="sm" variant="ghost" onClick={() => act(s, "cancel")} data-testid={`recent-sale-cancel-${s.number}`}><Ban className="h-4 w-4 mr-1" />Cancelar</Button>}
                    {canDo(user, "reopen_comanda") && s.origin === "balcao" && <Button size="sm" variant="ghost" onClick={() => act(s, "reopen")} data-testid={`recent-sale-reopen-${s.number}`}><RotateCcw className="h-4 w-4 mr-1" />Reabrir</Button>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>
      <ReceiptDialog sale={receipt} onClose={() => setReceipt(null)} />
      <PaymentDialog open={!!changing} onOpenChange={(o) => !o && setChanging(null)} subtotal={changing?.total || 0} allowDiscount={false} title={`Alterar pagamento da venda #${changing?.number || ""}`} onConfirm={changePayments} />
    </>
  );
}
